import React from 'react';
import { Box, FlexProps } from '@withjoy/joykit';
import { HighlightContent } from './HighlightContent';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useHighlightController } from './Highlight.controller';
import { HighlightAnimationContainer, HighlightColumn, HighlightColumnContent, styles } from './Highlight.styles';

export type HighlightVariants = 'babyOverview' | 'weddingTrack';

export type HighlightBackgroundVideo = {
  backgroundVideo?: {
    playerId: string;
    desktopVideoUrl: string;
    mobileVideoUrl: string;
  } | null;
};

export type HighlightActionButton = {
  path?: string;
  variant: 'checklist' | 'thankYouDialog' | 'mobileApp' | 'shopLink' | 'friendsRegistryLink' | 'quickAddLink';
  component: string;
  method: string;
};

export type HighlightProps = {
  type?: 'primary' | 'secondary';
  eyebrow?: string;
  title: string;
  text?: string;
  showCtaIcon?: boolean;
  variant?: HighlightVariants;
  buttonLabel: string;
  screenType?: 'mobile' | 'desktop';
  hideOnTablet?: boolean;
  colorMode?: 'light' | 'dark';
  eventId?: string;
  actionButton: HighlightActionButton;
  onOpen?: () => void;
} & FlexProps &
  HighlightBackgroundVideo;

export const Highlight = ({
  type = 'secondary',
  eyebrow,
  text,
  title,
  buttonLabel,
  screenType,
  hideOnTablet,
  backgroundVideo,
  colorMode = 'dark',
  onOpen,
  variant,
  showCtaIcon,
  actionButton,
  eventId,
  ...restProps
}: HighlightProps) => {
  const highlightContentProps = { type, eyebrow, text, title, colorMode, buttonLabel, variant, showCtaIcon };
  const { isMobileScreen, isDesktopScreen, onButtonClick } = useHighlightController(onOpen, eventId);
  const { value: enableAddFromFriendsRegistry } = useFeatureValue('enableAddFromFriendsRegistry');

  if (actionButton.variant === 'friendsRegistryLink' && enableAddFromFriendsRegistry !== 'on') {
    return null;
  }

  if (screenType === 'mobile' && !isMobileScreen) {
    return null;
  }

  if (screenType === 'desktop' && !isDesktopScreen) {
    return null;
  }

  if (hideOnTablet && !isDesktopScreen && !isMobileScreen) {
    return null;
  }

  return (
    <HighlightColumn span={type === 'primary' ? [12] : [12, 6, 4]} marginTop={6}>
      <Box paddingX={variant === 'babyOverview' && type === 'primary' ? [0, 6, 6, 0] : variant === 'babyOverview' && type === 'secondary' ? [6, 6, 6, 0] : 0}>
        <HighlightColumnContent __css={styles.highlightColumnContent({ type, variant, colorMode })} {...restProps}>
          {backgroundVideo && (
            <HighlightAnimationContainer __css={styles.highlightAnimationContainer({ backgroundImage: restProps.backgroundImage, variant })}>
              {isMobileScreen ? (
                <Box
                  as="iframe"
                  title={backgroundVideo.playerId}
                  id={backgroundVideo.playerId}
                  src={backgroundVideo.mobileVideoUrl}
                  frameBorder="0"
                  width="100%"
                  __css={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    height: 'calc(100% * 1.93)',
                    transform: 'translate(-50%,-50%)'
                  }}
                />
              ) : (
                <Box as="iframe" title={backgroundVideo.playerId} id={backgroundVideo.playerId} src={backgroundVideo.desktopVideoUrl} frameBorder="0" width="100%" height="100%" />
              )}
            </HighlightAnimationContainer>
          )}
          {(variant !== 'babyOverview' || type !== 'primary' || !isMobileScreen) && (
            <HighlightContent {...highlightContentProps} onButtonClick={() => onButtonClick(actionButton)} />
          )}
        </HighlightColumnContent>
        {variant === 'babyOverview' && type === 'primary' && isMobileScreen && (
          <HighlightContent {...highlightContentProps} onButtonClick={() => onButtonClick(actionButton)} padding={[8, 8, 0]} />
        )}
      </Box>
    </HighlightColumn>
  );
};
